import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, OnInit, output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import type { WaterTestimonial, WaterTestimonialCreateOrUpdate } from '@models/water-testimonial';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CodeListsService } from '@services/codelists.service';
import { ConfirmationService } from '@services/confirmation.service';
import { CrmWatersService } from '@services/crm-waters.service';
import { showGenericErrorToast } from '@services/show-generic-error-toast';
import { ToastService } from '@services/toast.service';
import { filter, firstValueFrom } from 'rxjs';
import { WaterTestimonialDialogComponent, WaterTestimonialDialogData } from './water-testimonial-dialog.component';

@Component({
  selector: 'app-water-testimonials',
  templateUrl: './water-testimonials.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    TranslatePipe,
  ],
})
export class WaterTestimonialsComponent implements OnInit {
  readonly waterId = input.required<number>();
  /** Emits the testimonials count so the parent reviews component can show it as a tab badge. */
  readonly totalCount = output<number>();

  private readonly watersService = inject(CrmWatersService);
  private readonly dialog = inject(MatDialog);
  private readonly confirmation = inject(ConfirmationService);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);
  readonly codeListsService = inject(CodeListsService);

  readonly testimonials = signal<WaterTestimonial[]>([]);
  readonly isLoading = signal(false);

  readonly displayedColumns = ['title', 'site', 'score', 'createdOn', 'editOn', 'actions'];

  ngOnInit(): void {
    this.load();
  }

  private load(): void {
    this.isLoading.set(true);
    this.watersService
      .getTestimonials(this.waterId())
      .pipe(
        showGenericErrorToast(
          this.toastService,
          () => [] as WaterTestimonial[],
          this.translate.instant('waters.testimonials.load-failed'),
        ),
      )
      .subscribe((items) => {
        this.testimonials.set(items);
        this.isLoading.set(false);
        this.totalCount.emit(items.length);
      });
  }

  siteName(siteId: number | null): string {
    if (siteId == null) return '—';
    return this.codeListsService.siteMap().get(siteId) ?? String(siteId);
  }

  add(): void {
    this.openDialog(null);
  }

  edit(testimonial: WaterTestimonial): void {
    this.openDialog(testimonial);
  }

  private openDialog(testimonial: WaterTestimonial | null): void {
    const data: WaterTestimonialDialogData = { testimonial };
    this.dialog
      .open<WaterTestimonialDialogComponent, WaterTestimonialDialogData, WaterTestimonialCreateOrUpdate>(
        WaterTestimonialDialogComponent,
        { data, width: '560px' },
      )
      .afterClosed()
      .pipe(filter((dto): dto is WaterTestimonialCreateOrUpdate => dto != null))
      .subscribe((dto) => {
        const request$ = testimonial
          ? this.watersService.updateTestimonial(this.waterId(), testimonial.id, dto)
          : this.watersService.createTestimonial(this.waterId(), dto);
        request$.subscribe({
          next: () => {
            this.toastService.showSuccess(this.translate.instant('waters.testimonials.save-success'));
            this.load();
          },
          error: () => this.toastService.showError(this.translate.instant('waters.testimonials.save-failed')),
        });
      });
  }

  async delete(testimonial: WaterTestimonial): Promise<void> {
    const confirmed = await this.confirmation.open({
      title: this.translate.instant('waters.testimonials.delete-title'),
      prompt: this.translate.instant('waters.testimonials.delete-prompt'),
    });
    if (!confirmed) return;

    try {
      await firstValueFrom(this.watersService.deleteTestimonial(this.waterId(), testimonial.id));
      this.toastService.showSuccess(this.translate.instant('waters.testimonials.delete-success'));
      this.load();
    } catch {
      this.toastService.showError(this.translate.instant('waters.testimonials.delete-failed'));
    }
  }
}
